import * as _ from "underscore";
import Big from "big.js";

import { EMDASH } from "@eave/shared/utils/general";
import { OrderedDecisionCriteria } from "@eave/shared/types/api";

import { IFieldDef } from "./interfaces";

/* field types */

export const FIELD_TYPE = {
  BOOL: "bool",
  DATE: "date",
  NUMBER: "number",
  TEXT: "text",
  WS: "ws",
  WS_SLICE: "ws_slice",
};

/* base field defs */

const fieldBool = (name: string): IFieldDef => ({
  field: name,
  type: FIELD_TYPE.BOOL,
  formatting: val => {
    if (val === null || val === "") {
      return EMDASH;
    }
    return val ? "Yes" : "No";
  },
});

const fieldNumber = (name: string, precision: number): IFieldDef => ({
  field: name,
  type: FIELD_TYPE.NUMBER,
  precision,
});

const fieldDate = (name: string): IFieldDef => ({
  field: name,
  type: FIELD_TYPE.DATE,
});

const fieldText = (name: string): IFieldDef => ({
  field: name,
  type: FIELD_TYPE.TEXT,
});

const fieldWorksheet = (name: string, wsName: string): IFieldDef => ({
  field: name,
  type: FIELD_TYPE.WS,
  wsName,
});

const fieldWorksheetSlice = (name: string, wsName: string): IFieldDef => ({
  field: name,
  type: FIELD_TYPE.WS_SLICE,
  wsName,
});

/* field def macros */

const fieldEnum = (name: string, enumValues: string[]): IFieldDef => ({
  ...fieldText(name),
  enumValues,
});

const fieldId = (): IFieldDef => ({
  ...fieldText("id"),
  hidden: true,
  readOnly: true,
});

const fieldMoney = (name: string): IFieldDef => ({
  ...fieldNumber(name, 2),
  formatting: val => Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" }),
});

const fieldVersion = (): IFieldDef => ({
  ...fieldNumber("version", 0),
  hidden: true,
  readOnly: true,
});

const fieldPercentage = (name: string, precision: number): IFieldDef => ({
  ...fieldNumber(name, precision),
  formatting: val => {
    const [, d] = `${val}`.split(".");
    return `${Number((val * 100).toFixed(Math.max((d && d.length - 2) || 0, 0)))}%`;
  },
});

// fieldPercentageAsInteger is the same as fieldPercentage except it displays
// the value as a human readable number instead of a decimal for editing.
const fieldPercentageAsHumanPercentage = (name: string, precision: number): IFieldDef => ({
  ...fieldNumber(name, precision),
  formatting: val => {
    const [, d] = `${val}`.split(".");
    return `${Number((+val).toFixed(Math.max((d && d.length - 2) || 0, precision - 2)))}%`;
  },
  beforeUpdate: (v: string) => (v ? new Big(v).div(100).toString() : v),
  transformValue: (v: string) => (v ? new Big(v).mul(100).toString() : v),
});

/* worksheet defs */

export const ENUM = {
  ADJUSTMENT_ITEM: [
    "DueFromBorrowerAtClosing",
    "DueFromSellerAtClosing",
    "DueToSellerAtClosing",
    "PaidAlreadyByOrOnBehalfOfBorrowerAtClosing",
  ],
  ESCROW_ITEM: [
    "AssessmentTax",
    "CityBondTax",
    "CityPropertyTax",
    "CondominiumAssociationDues",
    "CondominiumAssociationSpecialAssessment",
    "ConstructionCompletionFunds",
    "CooperativeAssociationDues",
    "CooperativeAssociationSpecialAssessment",
    "CountyBondTax",
    "CountyPropertyTax",
    "DistrictPropertyTax",
    "EarthquakeInsurance",
    "EnergyEfficientImprovementFunds",
    "FloodInsurance",
    "HailInsurancePremium",
    "HazardInsurance",
    "HomeownersAssociationDues",
    "HomeownersAssociationSpecialAssessment",
    "HomeownersInsurance",
    "MortgageInsurance",
    "Other",
    "ParishTax",
    "PestInsurance",
    "PropertyTax",
    "RehabilitationFunds",
    "SchoolPropertyTax",
    "StatePropertyTax",
    "TownPropertyTax",
    "TownshipPropertyTax",
    "VillagePropertyTax",
    "VolcanoInsurance",
    "WindstormInsurance",
  ],
  ESTIMATED_OR_ACTUAL: ["Estimated", "Actual"],
  RELATIONSHIP_STATUS: ["married", "unmarried", "separated"],
  PREPAID_PAID_BY: ["Broker", "Buyer", "Correspondent", "Lender", "Other", "Seller", "ThirdParty"],
  PREPAID_PAID_TO: [
    "AffiliateProvider",
    "Broker",
    "Investor",
    "Lender",
    "Other",
    "ThirdPartyProvider",
  ],
  PREPAID_TIMING: ["AtClosing", "BeforeClosing", "FirstPayment", "Other"],
  PREPAID_TYPE: [
    "BoroughPropertyTax",
    "CityPropertyTax",
    "CondominiumAssociationDues",
    "CondominiumAssociationSpecialAssessment",
    "CooperativeAssociationDues",
    "CooperativeAssociationSpecialAssessment",
    "CountyPropertyTax",
    "DistrictPropertyTax",
    "EarthquakeInsurancePremium",
    "FloodInsurancePremium",
    "HailInsurancePremium",
    "HazardInsurancePremium",
    "HomeownersAssociationDues",
    "HomeownersAssociationSpecialAssessment",
    "HomeownersInsurancePremium",
    "MortgageInsurancePremium",
    "Other",
    "PrepaidInterest",
    "SchoolPropertyTax",
    "StatePropertyTax",
    "TownPropertyTax",
    "VolcanoInsurancePremium",
    "WindAndStormInsurancePremium",
  ],
  PROPERTY_USAGE: ["primary", "secondary", "investment", "other"],
  PRORATION_ITEM: [
    "BoroughPropertyTax",
    "CityPropertyTax",
    "CondominiumAssociationDues",
    "CondominiumAssociationSpecialAssessment",
    "CooperativeAssociationDues",
    "CooperativeAssociationSpecialAssessment",
    "CountyPropertyTax",
    "DistrictPropertyTax",
    "EarthquakeInsurancePremium",
    "FloodInsurancePremium",
    "GroundRent",
    "HailInsurancePremium",
    "HazardInsurancePremium",
    "HomeownersAssociationDues",
    "HomeownersAssociationSpecialAssessment",
    "HomeownersInsurancePremium",
    "InterestOnLoanAssumption",
    "MortgageInsurancePremium",
    "Other",
    "PastDuePropertyTax",
    "RentFromSubjectProperty",
    "SchoolPropertyTax",
    "StatePropertyTax",
    "TownPropertyTax",
    "Utilities",
    "VolcanoInsurancePremium",
    "WindAndStormInsurancePremium",
  ],
  SELLER_CREDIT_METHODS: ["FixedAmount", "PercentageOfPurchasePrice"],
  SUFFIX: ["II", "III", "IV", "V", "VI", "Jr", "Sr"],
};

export type WorksheetName = keyof typeof WS_NAME;
export const WS_NAME = {
  ADJUSTMENT: "adjustment",
  ADJUSTMENTS: "adjustments",
  TRANSACTION_TERMS: "transaction_terms",
  ASSET_SUMMARY: "AssetSummary",
  CREDIT_SUMMARY: "CreditSummary",
  EMPLOYMENT: "Employment",
  EMPLOYMENT_INCOME: "EmploymentIncome",
  INCOME_SUMMARY: "IncomeSummary",
  OVERALL_SUMMARY: "OverallSummary",
  UNDERWRITING_SUMMARY: "UnderwritingSummary",
  ADDRESS: "address",
  BORROWER: "borrower",
  BORROWERS: "borrowers",
  BORROWER_ESTIMATES: "borrower_estimates",
  CASH_TO_CLOSE_ITEMS: "cash_to_close_items",
  DISBURSEMENT: "disbursement",
  ESCROWS: "escrows",
  ESCROW_ACCOUNT: "escrow_account",
  ESCROW_ITEM: "escrow_item",
  FANNIE_MAE_STATUS: "fannie_mae_status",
  LOAN_FILE: "loan_file",
  LOAN_FILE_DEFAULTS: "loan_file_defaults",
  MISCELLANEOUS: "miscellaneous",
  PREPAIDS: "prepaids",
  PREPAID_ITEM: "prepaid_item",
  PRORATION: "proration",
  PRORATIONS: "prorations",
  RESERVES: "reserves",
  RESERVES_FOR_PROPERTY: "reserves_for_property",
  RESERVE_REQUIREMENT: "reserve_requirement",
  SELLER_CREDITS: "seller_credits",
  // BEGIN generated with cmd generate_de_ws_definitions
  SNAPSHOT_ADDRESS: "snapshot_Address",
  SNAPSHOT_ALIAS_ACCOUNT: "snapshot_AliasAccount",
  SNAPSHOT_ASSET_INFORMATION: "snapshot_AssetInformation",
  SNAPSHOT_BANKRUPTCY: "snapshot_Bankruptcy",
  SNAPSHOT_BORROWER_INFORMATION: "snapshot_BorrowerInformation",
  SNAPSHOT_CIC_DATA: "snapshot_CICData",
  SNAPSHOT_CASH_TO_CLOSE_AMOUNTS: "snapshot_CashToCloseAmounts",
  SNAPSHOT_CLOSING_ADJUSTMENT: "snapshot_ClosingAdjustment",
  SNAPSHOT_COMBINED_MONTHLY_HOUSING_EXPENSE: "snapshot_CombinedMonthlyHousingExpense",
  SNAPSHOT_CREDIT_SCORE: "snapshot_CreditScore",
  SNAPSHOT_DECLARATIONS: "snapshot_Declarations",
  SNAPSHOT_DISCLOSURE_ENTITIES_VALUES: "snapshot_DisclosureEntitiesValues",
  SNAPSHOT_EMPLOYMENT_INFORMATION: "snapshot_EmploymentInformation",
  SNAPSHOT_ESCROW_DISBURSEMENT_META: "snapshot_EscrowDisbursementMeta",
  SNAPSHOT_ESCROW_META: "snapshot_EscrowMeta",
  SNAPSHOT_EXPENSE_INFORMATION: "snapshot_ExpenseInformation",
  SNAPSHOT_FACTOR: "snapshot_Factor",
  SNAPSHOT_FEE: "snapshot_Fee",
  SNAPSHOT_INCOME_INFORMATION: "snapshot_IncomeInformation",
  SNAPSHOT_LANDLORD: "snapshot_Landlord",
  SNAPSHOT_LANDLORD_DETAIL: "snapshot_LandlordDetail",
  SNAPSHOT_LENDER_CREDIT: "snapshot_LenderCredit",
  SNAPSHOT_LIABILITY_INFORMATION: "snapshot_LiabilityInformation",
  SNAPSHOT_MI_DATA: "snapshot_MIData",
  SNAPSHOT_MILITARY_SERVICE_DATA: "snapshot_MilitaryServiceData",
  SNAPSHOT_MONTHLY_PREPAID_META: "snapshot_MonthlyPrepaidMeta",
  SNAPSHOT_OTHER_INCOME: "snapshot_OtherIncome",
  SNAPSHOT_OWNED_PROPERTY: "snapshot_OwnedProperty",
  SNAPSHOT_PARTY_INFO: "snapshot_PartyInfo",
  SNAPSHOT_PRORATION_ITEM: "snapshot_ProrationItem",
  SNAPSHOT_RATE_CAPS: "snapshot_RateCaps",
  SNAPSHOT_RESIDENCE: "snapshot_Residence",
  SNAPSHOT_RESIDENCE_DETAIL: "snapshot_ResidenceDetail",
  SNAPSHOT_SETTLEMENT_AGENT: "snapshot_SettlementAgent",
  SNAPSHOT_SETTLEMENT_AGENT_FEE: "snapshot_SettlementAgentFee",
  SNAPSHOT_SNAPSHOT: "snapshot_Snapshot",
  SNAPSHOT_URLA_ENTITIES_VALUES: "snapshot_URLAEntitiesValues",
  // END generated with cmd generate_de_ws_definitions
};

export const WS_DEF = {
  [WS_NAME.ADJUSTMENT]: [
    fieldId(),
    fieldVersion(),
    { ...fieldText("section"), readOnly: true },
    fieldText("description"),
    { ...fieldText("type"), readOnly: true },
    fieldEnum("paid_to_paid_by", ENUM.ADJUSTMENT_ITEM),
    fieldMoney("final_le_amount"),
    fieldMoney("cd_amount"),
    fieldBool("verified"),
  ],
  [WS_NAME.TRANSACTION_TERMS]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("purchase_price", 0),
    fieldNumber("base_loan_amount", 0),
    fieldNumber("downpayment", 0),
  ],
  [WS_NAME.ADJUSTMENTS]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheetSlice("items", WS_NAME.ADJUSTMENT),
  ],
  [WS_NAME.ASSET_SUMMARY]: [
    fieldId(),
    fieldVersion(),
    fieldText("Comment"),
    { ...fieldMoney("Downpayment"), readOnly: true },
    { ...fieldMoney("ClosingCosts"), readOnly: true },
    { ...fieldMoney("EarnestMoneyDeposit"), readOnly: true },
    { ...fieldMoney("EstimatedCashToClose"), readOnly: true },
  ],
  [WS_NAME.CREDIT_SUMMARY]: [
    fieldId(),
    fieldVersion(),
    fieldText("Comment"),
    { ...fieldNumber("CompositeScore", 0), readOnly: true },
    { ...fieldMoney("TotalHousingDebts"), readOnly: true },
    { ...fieldMoney("TotalNonHousingDebts"), readOnly: true },
    { ...fieldMoney("TotalDebtAndExpensesOtherThanSubject"), readOnly: true },
    { ...fieldText("CreditReportDate"), readOnly: true },
    { ...fieldText("CreditReportExpiriyDate"), readOnly: true },
  ],
  [WS_NAME.EMPLOYMENT]: [
    fieldId(),
    fieldVersion(),
    { ...fieldDate("StartDate"), readOnly: true },
    { ...fieldText("Title"), readOnly: true },
    { ...fieldText("Employer"), readOnly: true },
    { ...fieldWorksheetSlice("EmploymentIncomes", WS_NAME.EMPLOYMENT_INCOME), readOnly: true },
    { ...fieldMoney("TotalMonthlyIncome"), readOnly: true },
  ],
  [WS_NAME.EMPLOYMENT_INCOME]: [
    fieldId(),
    fieldVersion(),
    fieldEnum("Type", ["Base", "Bonus", "Commission", "Other", "Overtime"]),
    fieldMoney("Amount"),
  ],
  [WS_NAME.INCOME_SUMMARY]: [
    fieldId(),
    fieldVersion(),
    fieldText("Comment"),
    { ...fieldWorksheetSlice("Borrower1Employment", WS_NAME.EMPLOYMENT), readOnly: true },
    { ...fieldWorksheetSlice("Borrower2Employment", WS_NAME.EMPLOYMENT), readOnly: true },
    { ...fieldMoney("TotalMonthlyIncomeForApplication"), readOnly: true },
  ],
  [WS_NAME.OVERALL_SUMMARY]: [
    fieldId(),
    fieldVersion(),
    fieldText("Comment"),
    { ...fieldText("Program"), readOnly: true },
    { ...fieldText("Occupancy"), readOnly: true },
    { ...fieldText("PropertyType"), readOnly: true },
    { ...fieldMoney("DeterminedHomeValue"), readOnly: true },
    { ...fieldMoney("RequestedLoanAmount"), readOnly: true },
    { ...fieldMoney("DownPayment"), readOnly: true },
    { ...fieldPercentage("LTV", 20), readOnly: true },
    { ...fieldPercentage("NoteRate", 20), readOnly: true },
    { ...fieldPercentage("QualifyingRate", 20), readOnly: true },
    { ...fieldMoney("QualifyingMonthlyPAndI"), readOnly: true },
    { ...fieldMoney("PropertyTaxes"), readOnly: true },
    { ...fieldMoney("HazardInsurance"), readOnly: true },
    { ...fieldMoney("HomeownerAssnDues"), readOnly: true },
    { ...fieldMoney("QualifyingMonthlyPitia"), readOnly: true },
    fieldMoney("OtherMonthlyDebts"),
    { ...fieldMoney("TotalDebt"), readOnly: true },
    { ...fieldMoney("QualifiedIncome"), readOnly: true },
    { ...fieldPercentage("BackendEndDTI", 20), readOnly: true },
    { ...fieldNumber("CreditScore", 0), readOnly: true },
    { ...fieldMoney("CurrentHousingExpense"), readOnly: true },
    { ...fieldPercentage("PaymentShock", 20), readOnly: true },
    { ...fieldBool("PassPaymentShockThreshold"), readOnly: true },
    { ...fieldBool("AdditionalReservesNeeded"), readOnly: true },
    fieldWorksheet("CreditSummary", WS_NAME.CREDIT_SUMMARY),
    fieldWorksheet("IncomeSummary", WS_NAME.INCOME_SUMMARY),
    fieldWorksheet("AssetSummary", WS_NAME.ASSET_SUMMARY),
  ],
  [WS_NAME.UNDERWRITING_SUMMARY]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("OverallSummary", WS_NAME.OVERALL_SUMMARY),
  ],
  [WS_NAME.ADDRESS]: [
    fieldId(),
    fieldVersion(),
    fieldText("line_1"),
    fieldText("line_2"),
    fieldText("city"),
    fieldText("state"),
    fieldText("postal_code"),
  ],
  [WS_NAME.BORROWER]: [
    fieldId(),
    fieldVersion(),
    fieldText("first_name"),
    fieldText("last_name"),
    fieldText("middle_name"),
    fieldEnum("suffix", ENUM.SUFFIX),
    fieldEnum("relationship_status", ENUM.RELATIONSHIP_STATUS),
    fieldText("email"),
    fieldText("dob"),
    fieldText("phone"),
    fieldMoney("estimated_monthly_income"),
  ],
  [WS_NAME.BORROWERS]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("borrower_1", WS_NAME.BORROWER),
    fieldWorksheet("borrower_2", WS_NAME.BORROWER),
  ],
  [WS_NAME.BORROWER_ESTIMATES]: [
    fieldId(),
    fieldVersion(),
    fieldEnum("property_type", [
      "SFR",
      "SFR-ATT",
      "PUD",
      "PUD-ATT",
      "Site Condo",
      "Low-Rise Condo",
      "Mid-Rise Condo",
      "High-Rise Condo",
      "Duplex",
      "Duplex-ATT",
      "Triplex",
      "Triplex-ATT",
      "Quadplex",
      "Quadplex-ATT",
      "other",
    ]),
    fieldEnum("property_usage", ENUM.PROPERTY_USAGE),
    fieldMoney("target_purchase_price"),
    fieldMoney("target_loan_amount"),
    fieldText("property_city"),
    fieldText("property_state"),
    fieldText("property_county"),
    fieldBool("one_or_more_borrowers_are_fthb"),
  ],
  [WS_NAME.CASH_TO_CLOSE_ITEMS]: [
    fieldId(),
    fieldVersion(),
    fieldMoney("total_closing_costs"),
    fieldMoney("closing_costs_paid_before_closing"),
    fieldMoney("closing_costs_financed"),
    fieldMoney("down_payment"),
    { ...fieldMoney("deposit") },
    fieldMoney("funds_for_borrower"),
    { ...fieldMoney("seller_credits") },
    fieldMoney("adjustments_and_other_credits"),
  ],
  [WS_NAME.DISBURSEMENT]: [
    fieldId(),
    fieldVersion(),
    fieldMoney("amount"),
    fieldDate("due_date"),
    fieldDate("period_covered_start_date"),
    fieldDate("period_covered_end_date"),
  ],
  [WS_NAME.ESCROWS]: [
    fieldId(),
    fieldVersion(),

    // HACK: jjw: no support yet to compute these from escrow_items, so we make
    // them read-only, then re-calc/update on the front-end via a one-off hack
    { ...fieldMoney("annual_total"), readOnly: true },
    { ...fieldMoney("monthly_total"), readOnly: true },

    fieldWorksheetSlice("escrow_items", WS_NAME.ESCROW_ITEM),
  ],
  [WS_NAME.ESCROW_ACCOUNT]: [
    fieldId(),
    fieldVersion(),
    fieldBool("escrow_tax_and_hazard_required_by_lender"),
    fieldBool("escrow_tax_and_hazard_requested_by_borrower"),
  ],
  [WS_NAME.ESCROW_ITEM]: [
    fieldId(),
    fieldVersion(),
    fieldBool("included"),
    fieldEnum("type", ENUM.ESCROW_ITEM),
    fieldMoney("total_amount"),
    fieldNumber("months_collected", 0),
    fieldText("recipient"),
    fieldText("source_document"),
    {
      ...fieldMoney("monthly_amount"),
      readOnly: true,
    },
    fieldEnum("estimated_or_actual", ENUM.ESTIMATED_OR_ACTUAL),
    fieldWorksheetSlice("disbursements", WS_NAME.DISBURSEMENT),
    {
      ...fieldBool("sum_of_disbursements_equals_total_amount"),
      readOnly: true,
    },
    fieldBool("verified"),
  ],
  [WS_NAME.FANNIE_MAE_STATUS]: [
    fieldId(),
    fieldVersion(),
    {
      ...fieldEnum("recommendation", [
        "ApproveEligible",
        "ApproveIneligible",
        "ExpandedApproval1Eligible",
        "ExpandedApproval1Ineligible",
        "ExpandedApproval2Eligible",
        "ExpandedApproval2Ineligible",
        "ExpandedApproval3Eligible",
        "ExpandedApproval3Ineligible",
        "ReferEligible",
        "ReferIneligible",
        "ReferWithCaution4",
      ]),

      // HACK: jjw: empty string is not technically an acceptable enum value here, but something
      // upstream is setting this value (vs. null) - this change makes sure that a character renders
      // to the screen so that users have something to click on and change (don't need to change
      // upstream for now because the enum is set correctly before this value is actually used)
      formatting: val => (val === "" ? EMDASH : val),
    },
    fieldText("identifier"),
    fieldText("description"),
  ],
  [WS_NAME.LOAN_FILE]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("adjustments", WS_NAME.ADJUSTMENTS),
    fieldWorksheet("prorations", WS_NAME.PRORATIONS),
    fieldWorksheet("prepaids", WS_NAME.PREPAIDS),
    fieldWorksheet("escrows", WS_NAME.ESCROWS),
    fieldWorksheet("escrow_account", WS_NAME.ESCROW_ACCOUNT),
    fieldWorksheet("cash_to_close_items", WS_NAME.CASH_TO_CLOSE_ITEMS),
    fieldWorksheet("fannie_mae_status", WS_NAME.FANNIE_MAE_STATUS),
    fieldWorksheet("seller_credits", WS_NAME.SELLER_CREDITS),
    fieldWorksheet("miscellaneous", WS_NAME.MISCELLANEOUS),
    fieldWorksheet("borrowers", WS_NAME.BORROWERS),
    fieldWorksheet("borrower_estimates", WS_NAME.BORROWER_ESTIMATES),
    fieldWorksheet("defaults", WS_NAME.LOAN_FILE_DEFAULTS),
    fieldWorksheet("last_disclosure_snapshot", WS_NAME.SNAPSHOT_SNAPSHOT),
    fieldWorksheet("reserves", WS_NAME.RESERVES),
    fieldWorksheet("UnderwritingSummary", WS_NAME.UNDERWRITING_SUMMARY),
    fieldWorksheet("transaction_terms", WS_NAME.TRANSACTION_TERMS),
  ],
  [WS_NAME.LOAN_FILE_DEFAULTS]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("operator_minimum_loan_amount", 0),
    fieldNumber("operator_max_loan_amount", 0),
    fieldNumber("operator_max_purchase_price", 0),
    fieldPercentageAsHumanPercentage("operator_max_dti", 4),
    fieldPercentageAsHumanPercentage("operator_max_ltv", 2),
    fieldMoney("reserves_for_max_affordability"),
    fieldPercentageAsHumanPercentage("operator_closing_cost_estimated_percent", 4),
    fieldBool("limit_new_plaid_connections"),
    fieldEnum("decision_criteria", OrderedDecisionCriteria),
    fieldBool("non_warrantable_condo"),
    fieldNumber("promo_rate_subtracter", 3),
    fieldEnum("tax_years_to_use", ["Minus1And2", "Minus2And3", "DocsData"]),
  ],
  [WS_NAME.PREPAIDS]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheetSlice("items", WS_NAME.PREPAID_ITEM),
  ],
  [WS_NAME.PREPAID_ITEM]: [
    fieldId(),
    fieldVersion(),
    fieldMoney("amount"),
    fieldNumber("number_of_months", 0),
    fieldText("recipient"),
    fieldEnum("timing_type", ENUM.PREPAID_TIMING),
    fieldEnum("paid_to", ENUM.PREPAID_PAID_TO),
    fieldEnum("paid_by", ENUM.PREPAID_PAID_BY),
    fieldEnum("item_type", ENUM.PREPAID_TYPE),
  ],
  [WS_NAME.PRORATION]: [
    fieldId(),
    fieldVersion(),
    fieldText("description"),
    fieldEnum("type", ENUM.PRORATION_ITEM),
    fieldText("paid_to"),
    fieldDate("period_covered_start_date"),
    fieldDate("period_covered_end_date"),
    fieldBool("closing_date_paid_by_buyer"),
    fieldMoney("final_le_amount"),
    fieldMoney("cd_amount"),
    fieldBool("verified"),
  ],
  [WS_NAME.PRORATIONS]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheetSlice("paid_by_seller", WS_NAME.PRORATION),
    fieldWorksheetSlice("unpaid_by_seller", WS_NAME.PRORATION),
  ],
  [WS_NAME.RESERVES]: [
    fieldId(),
    fieldVersion(),
    fieldBool("use_instead_of_estimates"),
    {
      ...fieldMoney("total"),
      readOnly: true,
    },
    fieldMoney("operator_override"),
    {
      ...fieldText("operator_override_reason"),
      shownIf: inst => inst.operator_override !== null,
    },
    {
      ...fieldMoney("computed_total"),
      readOnly: true,
    },
    fieldWorksheetSlice("reserves", WS_NAME.RESERVES_FOR_PROPERTY),
  ],
  [WS_NAME.RESERVES_FOR_PROPERTY]: [
    fieldId(),
    fieldVersion(),
    fieldText("property"),
    {
      ...fieldMoney("total_amount_of_reserves_required"),
      readOnly: true,
    },
    fieldMoney("pitia"),
    {
      ...fieldNumber("total_months", 0),
      readOnly: true,
    },
    fieldWorksheetSlice("reserve_requirements", WS_NAME.RESERVE_REQUIREMENT),
    fieldNumber("borrower_entity", 0),
  ],
  [WS_NAME.RESERVE_REQUIREMENT]: [
    fieldId(),
    fieldVersion(),
    fieldText("reason"),
    fieldNumber("number_of_months", 0),
  ],
  [WS_NAME.SELLER_CREDITS]: [
    fieldId(),
    fieldVersion(),
    fieldEnum("method", ["FixedAmount", "PercentageOfPurchasePrice"]),
    {
      ...fieldMoney("fixed_amount"),
      shownIf: inst => inst.method === "FixedAmount",
      readOnly: true,
    },
    {
      ...fieldMoney("purchase_price"),
      shownIf: inst => inst.method === "PercentageOfPurchasePrice",
      readOnly: true,
    },
    {
      ...fieldNumber("percentage_of_purchase_price", 2),
      shownIf: inst => inst.method === "PercentageOfPurchasePrice",
    },
    {
      ...fieldMoney("percentage_amount"),
      shownIf: inst => inst.method === "PercentageOfPurchasePrice",
      readOnly: true,
    },
    {
      ...fieldMoney("fixed_or_percentage_amount"),
      hidden: true,
      readOnly: true,
    },
    fieldMoney("manual_override_amount"),
    {
      ...fieldMoney("final_amount"),
      readOnly: true,
    },
  ],
  [WS_NAME.MISCELLANEOUS]: [
    fieldId(),
    fieldVersion(),
    fieldText("title_only_name1"),
    fieldText("title_only_name2"),
    fieldText("title_only_name3"),
    fieldText("title_only_name4"),
    {
      ...fieldText("purchase_timeframe"),
      hidden: true,
    },
    {
      ...fieldBool("has_real_estate_agent"),
      hidden: true,
    },
    {
      ...fieldText("real_estate_agent_name"),
      hidden: true,
    },
    {
      ...fieldBool("wants_intros_to_agents"),
      hidden: true,
    },
    fieldText("lead_homelight_id"),
  ],
  // BEGIN generated with cmd generate_de_ws_definitions
  [WS_NAME.SNAPSHOT_ADDRESS]: [
    fieldId(),
    fieldVersion(),
    fieldText("AddressAdditionalLineText"),
    fieldText("AddressLineText"),
    fieldText("AddressType"),
    fieldText("AddressUnitIdentifier"),
    fieldText("CityName"),
    fieldText("CountryCode"),
    fieldText("CountyCode"),
    fieldText("CountyName"),
    fieldText("PostalCode"),
    fieldText("StateCode"),
  ],
  [WS_NAME.SNAPSHOT_ALIAS_ACCOUNT]: [
    fieldId(),
    fieldVersion(),
    fieldText("AlternateName"),
    fieldText("CreditorName"),
    fieldText("AccountID"),
  ],
  [WS_NAME.SNAPSHOT_ASSET_INFORMATION]: [
    fieldId(),
    fieldVersion(),
    fieldText("AccountType"),
    fieldText("AccountTypeOtherDescription"),
    fieldText("AccountID"),
    fieldText("AccountName"),
    fieldWorksheet("Address", WS_NAME.SNAPSHOT_ADDRESS),
    fieldNumber("Amount", 6),
    fieldNumber("UsableValue", 6),
    fieldBool("IncludedInAssetAccount"),
    fieldText("FundsSourceType"),
    fieldText("FundsSourceTypeOtherDescription"),
  ],
  [WS_NAME.SNAPSHOT_BANKRUPTCY]: [fieldId(), fieldVersion(), fieldText("BankruptcyChapter")],
  [WS_NAME.SNAPSHOT_BORROWER_INFORMATION]: [
    fieldId(),
    fieldVersion(),
    fieldText("FirstName"),
    fieldText("MiddleName"),
    fieldText("LastName"),
    fieldText("FullName"),
    fieldText("SSN"),
    fieldText("DOB"),
    fieldText("Email"),
    fieldText("Phone"),
    fieldText("MaritalStatus"),
    fieldWorksheet("PresentAddress", WS_NAME.SNAPSHOT_RESIDENCE),
    fieldWorksheetSlice("FormerAddress", WS_NAME.SNAPSHOT_RESIDENCE),
    fieldText("Suffix"),
    fieldWorksheetSlice("Bankruptcies", WS_NAME.SNAPSHOT_BANKRUPTCY),
  ],
  [WS_NAME.SNAPSHOT_CIC_DATA]: [
    fieldId(),
    fieldVersion(),
    fieldText("Description"),
    fieldText("OccurrenceDate"),
  ],
  [WS_NAME.SNAPSHOT_CASH_TO_CLOSE_AMOUNTS]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("TotalClosingCosts", 6),
    fieldNumber("ClosingCostsPaidBeforeClosing", 6),
    fieldNumber("ClosingCostsFinanced", 6),
    fieldNumber("Downpayment", 6),
    fieldNumber("Deposit", 6),
    fieldNumber("FundsForBorrower", 6),
    fieldNumber("SellerCredits", 6),
    fieldNumber("AdjustmentsAndOtherCredits", 6),
  ],
  [WS_NAME.SNAPSHOT_CLOSING_ADJUSTMENT]: [
    fieldId(),
    fieldVersion(),
    fieldText("Description"),
    fieldNumber("Amount", 6),
    fieldText("Type"),
    fieldText("IntegratedDisclosureSectionType"),
    fieldText("IntegratedDisclosureSubsectionType"),
    fieldBool("PaidOutsideOfClosing"),
  ],
  [WS_NAME.SNAPSHOT_COMBINED_MONTHLY_HOUSING_EXPENSE]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("Rent", 6),
    fieldNumber("FirstMortgage", 6),
    fieldNumber("OtherFinancing", 6),
    fieldNumber("RealEstateTaxes", 6),
    fieldNumber("HazardInsurance", 6),
    fieldNumber("HomeownerAssnDues", 6),
    fieldNumber("Other", 6),
    fieldNumber("MI", 6),
    fieldNumber("FloodInsurance", 6),
  ],
  [WS_NAME.SNAPSHOT_CREDIT_SCORE]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("CreditScore", 0),
    fieldText("RetrievedDate"),
    fieldText("Bureau"),
    fieldWorksheetSlice("Factors", WS_NAME.SNAPSHOT_FACTOR),
  ],
  [WS_NAME.SNAPSHOT_DECLARATIONS]: [
    fieldId(),
    fieldVersion(),
    fieldBool("Judgements"),
    fieldBool("Bankruptcy"),
    fieldBool("Foreclosure"),
    fieldBool("Lawsuit"),
    fieldBool("LoanForclosure"),
    fieldBool("Delinquency"),
    fieldBool("Alimony"),
    fieldBool("BorrowedDownpayment"),
    fieldBool("ComakerOrEndorser"),
    fieldText("CitizenshipResidencyType"),
    fieldText("IntentToOccupyType"),
    fieldText("HomeownerPastThreeYearsType"),
    fieldBool("BorrowerFirstTimeHomebuyerIndicator"),
    fieldText("PriorPropertyUsageType"),
    fieldText("PriorPropertyTitleType"),
    fieldBool("FHASecondaryResidenceIndicator"),
    fieldBool("SpecialBorrowerSellerRelationshipIndicator"),
    fieldBool("UndisclosedBorrowedFundsIndicator"),
    fieldNumber("UndisclosedBorrowedFundsAmount", 6),
    fieldBool("UndisclosedMortgageApplicationIndicator"),
    fieldBool("UndisclosedCreditApplicationIndicator"),
    fieldBool("PropertyProposedCleanEnergyLienIndicator"),
    fieldBool("UndisclosedComakerOfNoteIndicator"),
    fieldBool("PriorPropertyDeedInLieuConveyedIndicator"),
    fieldBool("PriorPropertyShortSaleCompletedIndicator"),
  ],
  [WS_NAME.SNAPSHOT_DISCLOSURE_ENTITIES_VALUES]: [
    fieldId(),
    fieldVersion(),
    fieldText("ExternalLoanId"),
    fieldText("MersID"),
    fieldText("LeadNumber"),
    fieldText("ApplicationDate"),
    fieldText("LoanProgram"),
    fieldText("LoanProductIdentifier"),
    fieldNumber("APR", 6),
    fieldNumber("NoteRate", 6),
    fieldNumber("QualifyingRate", 6),
    fieldNumber("LockDurationDays", 0),
    fieldText("RateLockedAt"),
    fieldNumber("MonthlyPayment", 6),
    fieldNumber("LoanAmount", 6),
    fieldNumber("AssumedLoanAmount", 6),
    fieldNumber("Margin", 6),
    fieldNumber("Index", 6),
    fieldNumber("Deposit", 6),
    fieldNumber("ExcessDeposit", 6),
    fieldWorksheet("PurchasedPropertyAddress", WS_NAME.SNAPSHOT_ADDRESS),
    fieldText("PurchasedPropertyType"),
    fieldText("PurchasedPropertyUsage"),
    fieldNumber("AppraisedValue", 6),
    fieldText("FloodZone"),
    fieldText("ProjectName"),
    fieldBool("MixedUse"),
    fieldWorksheetSlice("EscrowedItems", WS_NAME.SNAPSHOT_ESCROW_META),
    fieldWorksheetSlice("PrepaidItems", WS_NAME.SNAPSHOT_MONTHLY_PREPAID_META),
    fieldWorksheetSlice("AllClosingFees", WS_NAME.SNAPSHOT_FEE),
    fieldWorksheet("LastLECashToCloseItems", WS_NAME.SNAPSHOT_CASH_TO_CLOSE_AMOUNTS),
    fieldWorksheetSlice("ClosingAdjustments", WS_NAME.SNAPSHOT_CLOSING_ADJUSTMENT),
    fieldNumber("SellerCredits", 6),
    fieldWorksheetSlice("ProrationItems", WS_NAME.SNAPSHOT_PRORATION_ITEM),
    fieldText("CurrentRateSetDate"),
    fieldText("ClosingDate"),
    fieldText("FirstPaymentDate"),
    fieldNumber("PrepaidInterestPerDiem", 6),
    fieldNumber("PrepaidInterestDuration", 0),
    fieldNumber("PrepaidInterestTotalDue", 6),
    fieldText("EstimateAvailableThrough"),
    fieldText("LERateAvailableThrough"),
    fieldText("CDRateAvailableThrough"),
    fieldText("DisbursementDate"),
    fieldText("SigningDate"),
    fieldText("IssueDate"),
    fieldText("LastDisclosureStage"),
    fieldText("LastDisclosureTime"),
    fieldText("LastDisclosureReceivedTime"),
    fieldNumber("LastDisclosedRate", 6),
    fieldNumber("LastDisclosedAPR", 6),
    fieldWorksheetSlice("LenderCredits", WS_NAME.SNAPSHOT_LENDER_CREDIT),
    fieldNumber("TridCure", 6),
    fieldNumber("AggregateAdjustment", 6),
    fieldWorksheet("SettlementAgent", WS_NAME.SNAPSHOT_SETTLEMENT_AGENT),
    fieldWorksheetSlice("SuggestedSettlementAgents", WS_NAME.SNAPSHOT_SETTLEMENT_AGENT),
    fieldWorksheetSlice("Sellers", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldWorksheet("SellerRealEstateAgent", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldWorksheet("BuyerRealEstateAgent", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldText("UnderwriterComments"),
    fieldNumber("VerifiedAssetsTotal", 6),
    fieldWorksheetSlice("Borrower1CreditScores", WS_NAME.SNAPSHOT_CREDIT_SCORE),
    fieldWorksheetSlice("Borrower2CreditScores", WS_NAME.SNAPSHOT_CREDIT_SCORE),
    fieldWorksheet("Appraiser", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldWorksheet("Underwriter", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldWorksheet("LoanOfficer", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldText("DURecommendation"),
    fieldNumber("LTV", 6),
    fieldNumber("CLTV", 6),
    fieldNumber("HousingExpenseRatio", 6),
    fieldNumber("DebtToIncomeRatio", 6),
    fieldText("TitleOnlyName1"),
    fieldWorksheet("RateCaps", WS_NAME.SNAPSHOT_RATE_CAPS),
    fieldNumber("DownPayment", 6),
    fieldText("SubjectPropertyState"),
    fieldBool("EscrowTaxAndHazardRequiredByLender"),
    fieldBool("EscrowTaxAndHazardRequestedByBorrower"),
    fieldBool("HasEscrowAccount"),
    fieldText("FNMAIdentifier"),
    fieldText("ChannelName"),
    fieldText("Title"),
    fieldText("TitleLink"),
    fieldText("TitleEng"),
    fieldWorksheet("Trustee", WS_NAME.SNAPSHOT_PARTY_INFO),
    fieldText("LenderName"),
    fieldWorksheet("MortgageInsurance", WS_NAME.SNAPSHOT_MI_DATA),
    fieldText("CreditApplicationDate"),
    fieldText("TitleOnlyName2"),
    fieldText("TitleOnlyName3"),
    fieldText("TitleOnlyName4"),
    fieldWorksheet("ChangeInCircumstance", WS_NAME.SNAPSHOT_CIC_DATA),
    fieldText("ReturnEscrowAbsenceReason"),
    fieldNumber("InterestedPartyContributionsRatio", 6),
    fieldWorksheetSlice("Borrower3CreditScores", WS_NAME.SNAPSHOT_CREDIT_SCORE),
    fieldWorksheetSlice("Borrower4CreditScores", WS_NAME.SNAPSHOT_CREDIT_SCORE),
    fieldBool("LocatedInProject"),
    fieldText("ProjectClassificationIdentifier"),
    fieldWorksheet("Borrower1MilitaryService", WS_NAME.SNAPSHOT_MILITARY_SERVICE_DATA),
    fieldWorksheet("Borrower2MilitaryService", WS_NAME.SNAPSHOT_MILITARY_SERVICE_DATA),
    fieldWorksheet("Borrower3MilitaryService", WS_NAME.SNAPSHOT_MILITARY_SERVICE_DATA),
    fieldWorksheet("Borrower4MilitaryService", WS_NAME.SNAPSHOT_MILITARY_SERVICE_DATA),
    fieldText("ClosingOrderID"),
    fieldText("MortgageType"),
    fieldText("HUDVAAgencyCaseNumber"),
    fieldText("VATitleVestingType"),
    fieldText("VATitleVestingTypeOtherDescription"),
    fieldText("FHAVALoanPurposeType"),
    fieldBool("PreviousVAHomeLoan"),
    fieldText("FHAVASalesPriceExceedsAppraisedValueType"),
    fieldText("VAOccupancy"),
    fieldBool("Assumable"),
  ],
  [WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION]: [
    fieldId(),
    fieldVersion(),
    fieldText("Name"),
    fieldText("Position"),
    fieldWorksheet("Address", WS_NAME.SNAPSHOT_ADDRESS),
    fieldText("StartDate"),
    fieldText("EndDate"),
    fieldText("TelephoneNumber"),
    fieldNumber("MonthlyIncome", 6),
    fieldBool("IsSelfEmployed"),
    fieldNumber("YearsInLineOfWork", 0),
    fieldBool("EmployerIsPartyToTransaction"),
    fieldNumber("EmploymentID", 0),
    fieldEnum("OwnershipInterestType", ["GreaterThanOrEqualTo25Percent", "LessThan25Percent"]),
  ],
  [WS_NAME.SNAPSHOT_ESCROW_DISBURSEMENT_META]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("Amount", 6),
    fieldText("DueDate"),
    fieldText("PeriodCoveredStart"),
    fieldText("PeriodCoveredEnd"),
  ],
  [WS_NAME.SNAPSHOT_ESCROW_META]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("TotalAmount", 6),
    fieldText("EscrowType"),
    fieldNumber("EscrowReserveMonths", 0),
    fieldWorksheetSlice("Disbursements", WS_NAME.SNAPSHOT_ESCROW_DISBURSEMENT_META),
    fieldText("Recipient"),
    fieldText("SourceDocS3Key"),
  ],
  [WS_NAME.SNAPSHOT_EXPENSE_INFORMATION]: [
    fieldId(),
    fieldVersion(),
    fieldText("Recipient"),
    fieldNumber("ExpenseMonthlyPaymentAmount", 6),
    fieldNumber("LinkedToBorrower", 0),
    fieldText("ExpenseType"),
  ],
  [WS_NAME.SNAPSHOT_FACTOR]: [fieldId(), fieldVersion(), fieldText("Code"), fieldText("Text")],
  [WS_NAME.SNAPSHOT_FEE]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("EstimatedAmount", 6),
    fieldNumber("ActualAmount", 6),
    fieldText("MismoType"),
    fieldText("Description"),
    fieldText("PaidByType"),
    fieldText("PaidToType"),
    fieldText("Recipient"),
    fieldText("LESection"),
    fieldBool("IsAPR"),
    fieldBool("IsPaidOutsideClosing"),
    fieldBool("IsFinanced"),
    fieldText("CDSection"),
  ],
  [WS_NAME.SNAPSHOT_INCOME_INFORMATION]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("BaseEmploymentIncome", 6),
    fieldNumber("Overtime", 6),
    fieldNumber("Bonuses", 6),
    fieldNumber("Commissions", 6),
    fieldNumber("DividendsInterest", 6),
    fieldNumber("NetRentalIncome", 6),
    fieldNumber("SubjectPropertyNetCashFlow", 6),
    fieldWorksheetSlice("Other", WS_NAME.SNAPSHOT_OTHER_INCOME),
    fieldText("OwnerEntityType"),
    fieldNumber("OwnerEntityID", 0),
  ],
  [WS_NAME.SNAPSHOT_LANDLORD]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("LandlordDetail", WS_NAME.SNAPSHOT_LANDLORD_DETAIL),
  ],
  [WS_NAME.SNAPSHOT_LANDLORD_DETAIL]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("MonthlyRentAmount", 6),
  ],
  [WS_NAME.SNAPSHOT_LENDER_CREDIT]: [
    fieldId(),
    fieldVersion(),
    fieldText("Description"),
    fieldNumber("Amount", 6),
  ],
  [WS_NAME.SNAPSHOT_LIABILITY_INFORMATION]: [
    fieldId(),
    fieldVersion(),
    fieldText("AccountID"),
    fieldNumber("MonthlyPaymentAmount", 6),
    fieldNumber("RemainingMonthsCount", 0),
    fieldNumber("UnpaidBalanceAmount", 6),
    fieldText("LiabilityType"),
    fieldBool("ExclusionIndicator"),
    fieldBool("PaidByClosing"),
    fieldText("LiabilityHolderName"),
    fieldWorksheet("LiabilityHolderAddress", WS_NAME.SNAPSHOT_ADDRESS),
    fieldBool("IsForRentalProperty"),
    fieldNumber("LinkedToBorrower", 0),
    fieldNumber("LinkedToOwnedRealEstateID", 0),
    fieldBool("ExcludeForDU"),
    fieldText("MortgageType"),
    fieldNumber("HELOCCreditLimit", 6),
    fieldBool("PaidByClosingForDU"),
  ],
  [WS_NAME.SNAPSHOT_MI_DATA]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("Coverage", 6),
    fieldNumber("InitialRate", 6),
    fieldNumber("MonthlyPremiumDoNotUse", 6),
    fieldNumber("CountOfMonthlyPayments", 0),
    fieldText("CertificateIdentifier"),
    fieldNumber("Term2To10Rate", 6),
    fieldNumber("Term11Rate", 6),
    fieldNumber("LoanAmount", 6),
    fieldNumber("PremiumDueToActivate", 6),
  ],
  [WS_NAME.SNAPSHOT_MILITARY_SERVICE_DATA]: [
    fieldId(),
    fieldVersion(),
    fieldBool("EverServe"),
    fieldDate("ActiveDutyExpiration"),
    fieldBool("RetiredDischargedSeparated"),
    fieldBool("NonActivatedReserve"),
    fieldBool("SurvivingSpouse"),
  ],
  [WS_NAME.SNAPSHOT_MONTHLY_PREPAID_META]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("Amount", 6),
    fieldNumber("NumMonths", 0),
    fieldText("Recipient"),
    fieldText("TimingType"),
    fieldText("PaidBy"),
    fieldText("PaidTo"),
    fieldText("ItemType"),
  ],
  [WS_NAME.SNAPSHOT_OTHER_INCOME]: [
    fieldId(),
    fieldVersion(),
    fieldText("Description"),
    fieldNumber("Amount", 6),
  ],
  [WS_NAME.SNAPSHOT_OWNED_PROPERTY]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("Address", WS_NAME.SNAPSHOT_ADDRESS),
    fieldText("Disposition"),
    fieldText("PropertyType"),
    fieldNumber("LivingUnitCount", 0),
    fieldNumber("PresentMarketValue", 6),
    fieldNumber("AmountOfMortgageAndLiens", 6),
    fieldNumber("GrossRentalIncome", 6),
    fieldNumber("GrossRentalIncomeUnadjusted", 6),
    fieldNumber("MortgagePayments", 6),
    fieldNumber("MaintenanceExpense", 6),
    fieldNumber("NetRentalIncome", 6),
    fieldBool("IsCurrentResidence"),
    fieldNumber("LinkToOwnedRealEstateID", 0),
    fieldText("PropertyUsageType"),
  ],
  [WS_NAME.SNAPSHOT_PARTY_INFO]: [
    fieldId(),
    fieldVersion(),
    fieldText("Name"),
    fieldText("StreetAddress"),
    fieldText("City"),
    fieldText("State"),
    fieldText("PostalCode"),
    fieldText("Phone"),
    fieldText("ContactName"),
    fieldText("ContactEmail"),
    fieldText("License"),
    fieldText("StateLicense"),
    fieldText("ContactLicense"),
    fieldText("ContactStateLicense"),
  ],
  [WS_NAME.SNAPSHOT_PRORATION_ITEM]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("Amount", 6),
    fieldText("From"),
    fieldText("To"),
    fieldText("Type"),
    fieldText("Description"),
    fieldBool("PaidBySeller"),
  ],
  [WS_NAME.SNAPSHOT_RATE_CAPS]: [
    fieldId(),
    fieldVersion(),
    fieldNumber("InitialCapPercent", 6),
    fieldNumber("YearlyCapPercent", 6),
    fieldNumber("MaxCapPercent", 6),
  ],
  [WS_NAME.SNAPSHOT_RESIDENCE]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("Address", WS_NAME.SNAPSHOT_ADDRESS),
    fieldWorksheet("ResidenceDetail", WS_NAME.SNAPSHOT_RESIDENCE_DETAIL),
    fieldWorksheet("Landlord", WS_NAME.SNAPSHOT_LANDLORD),
  ],
  [WS_NAME.SNAPSHOT_RESIDENCE_DETAIL]: [
    fieldId(),
    fieldVersion(),
    fieldText("BorrowerResidencyBasisType"),
    fieldNumber("BorrowerResidencyDurationMonthsCount", 0),
    fieldNumber("BorrowerResidencyDurationYearsCount", 0),
    fieldText("BorrowerResidencyType"),
  ],
  [WS_NAME.SNAPSHOT_SETTLEMENT_AGENT]: [
    fieldId(),
    fieldVersion(),
    fieldText("ClosingCorpID"),
    fieldText("Name"),
    fieldText("StreetAddress"),
    fieldText("City"),
    fieldText("State"),
    fieldText("PostalCode"),
    fieldText("Phone"),
    fieldText("Website"),
    fieldText("ContactName"),
    fieldText("ContactEmail"),
    fieldText("StateLicense"),
    fieldText("ContactStateLicense"),
    fieldText("RateSheetID"),
    fieldWorksheetSlice("Fees", WS_NAME.SNAPSHOT_SETTLEMENT_AGENT_FEE),
  ],
  [WS_NAME.SNAPSHOT_SETTLEMENT_AGENT_FEE]: [
    fieldId(),
    fieldVersion(),
    fieldText("Description"),
    fieldNumber("Amount", 6),
  ],
  [WS_NAME.SNAPSHOT_SNAPSHOT]: [
    fieldId(),
    fieldVersion(),
    fieldWorksheet("DE", WS_NAME.SNAPSHOT_DISCLOSURE_ENTITIES_VALUES),
    fieldWorksheet("UE", WS_NAME.SNAPSHOT_URLA_ENTITIES_VALUES),
  ],
  [WS_NAME.SNAPSHOT_URLA_ENTITIES_VALUES]: [
    fieldId(),
    fieldVersion(),
    fieldText("LegalDescription"),
    fieldNumber("YearBuilt", 0),
    fieldText("MannerInWhichTitleWillBeHeld"),
    fieldText("VestingToRead"),
    fieldText("PropertyEstateType"),
    fieldText("SourceOfDownPayment"),
    fieldWorksheet("Borrower1", WS_NAME.SNAPSHOT_BORROWER_INFORMATION),
    fieldWorksheet("Borrower2", WS_NAME.SNAPSHOT_BORROWER_INFORMATION),
    fieldWorksheet("Borrower1PrimaryEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheetSlice("Borrower1OtherEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheet("Borrower2PrimaryEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheetSlice("Borrower2OtherEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheet("Borrower1Income", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheet("Borrower2Income", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheet(
      "PresentMonthlyHousingExpense",
      WS_NAME.SNAPSHOT_COMBINED_MONTHLY_HOUSING_EXPENSE,
    ),
    fieldWorksheet(
      "ProposedMonthlyHousingExpense",
      WS_NAME.SNAPSHOT_COMBINED_MONTHLY_HOUSING_EXPENSE,
    ),
    fieldBool("Is1003Joint"),
    fieldWorksheetSlice("Borrower1Assets", WS_NAME.SNAPSHOT_ASSET_INFORMATION),
    fieldWorksheetSlice("Borrower2Assets", WS_NAME.SNAPSHOT_ASSET_INFORMATION),
    fieldWorksheetSlice("Liabilities", WS_NAME.SNAPSHOT_LIABILITY_INFORMATION),
    fieldWorksheetSlice("OtherLiabilitiesAndExpenses", WS_NAME.SNAPSHOT_EXPENSE_INFORMATION),
    fieldWorksheetSlice("Borrower1OwnedProperties", WS_NAME.SNAPSHOT_OWNED_PROPERTY),
    fieldWorksheetSlice("Borrower1AliasAccounts", WS_NAME.SNAPSHOT_ALIAS_ACCOUNT),
    fieldWorksheetSlice("Borrower2AliasAccounts", WS_NAME.SNAPSHOT_ALIAS_ACCOUNT),
    fieldNumber("PurchasePrice", 6),
    fieldNumber("EstimatedPrepaidItems", 6),
    fieldNumber("EstimatedClosingCosts", 6),
    fieldWorksheet("Borrower1Declarations", WS_NAME.SNAPSHOT_DECLARATIONS),
    fieldWorksheet("Borrower2Declarations", WS_NAME.SNAPSHOT_DECLARATIONS),
    fieldWorksheetSlice("Borrower2OwnedProperties", WS_NAME.SNAPSHOT_OWNED_PROPERTY),
    fieldWorksheet("Borrower3", WS_NAME.SNAPSHOT_BORROWER_INFORMATION),
    fieldWorksheet("Borrower3PrimaryEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheetSlice("Borrower3OtherEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheet("Borrower3Income", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheetSlice("Borrower3Assets", WS_NAME.SNAPSHOT_ASSET_INFORMATION),
    fieldWorksheetSlice("Borrower3AliasAccounts", WS_NAME.SNAPSHOT_ALIAS_ACCOUNT),
    fieldWorksheet("Borrower3Declarations", WS_NAME.SNAPSHOT_DECLARATIONS),
    fieldWorksheetSlice("Borrower3OwnedProperties", WS_NAME.SNAPSHOT_OWNED_PROPERTY),
    fieldWorksheet("Borrower4", WS_NAME.SNAPSHOT_BORROWER_INFORMATION),
    fieldWorksheet("Borrower4PrimaryEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheetSlice("Borrower4OtherEmployment", WS_NAME.SNAPSHOT_EMPLOYMENT_INFORMATION),
    fieldWorksheet("Borrower4Income", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheetSlice("Borrower4Assets", WS_NAME.SNAPSHOT_ASSET_INFORMATION),
    fieldWorksheetSlice("Borrower4AliasAccounts", WS_NAME.SNAPSHOT_ALIAS_ACCOUNT),
    fieldWorksheet("Borrower4Declarations", WS_NAME.SNAPSHOT_DECLARATIONS),
    fieldWorksheetSlice("Borrower4OwnedProperties", WS_NAME.SNAPSHOT_OWNED_PROPERTY),
    fieldWorksheetSlice("Borrower1Incomes", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheetSlice("Borrower2Incomes", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheetSlice("Borrower3Incomes", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldWorksheetSlice("Borrower4Incomes", WS_NAME.SNAPSHOT_INCOME_INFORMATION),
    fieldEnum("URLAVersion", ["Effective2021", "Revised2009"]),
  ],
  // END generated with cmd generate_de_ws_definitions
};

/* worksheet ui */

const truncate = s => (s.length > 16 ? `${s.substr(0, 16)}…` : s);

export const WS_SLUG = {
  [WS_NAME.ESCROW_ITEM]: ws => {
    if (ws.instance && ws.instance.type) {
      return `Escrow item: ${truncate(ws.instance.type)}`;
    }
    return "Escrow item";
  },
  [WS_NAME.PREPAID_ITEM]: ws => {
    if (ws.instance && ws.instance.recipient) {
      return `Prepaid item: ${truncate(ws.instance.recipient)}`;
    }
    return "Prepaid item";
  },
  [WS_NAME.PRORATION]: ws => {
    if (ws.instance && ws.instance.description) {
      return `Proration: ${truncate(ws.instance.description)}`;
    }
    return "Proration";
  },
};

/* helpers */

export const getFieldDef = (wsName, field): IFieldDef => {
  const wsDef = WS_DEF[wsName];
  if (!wsDef) {
    throw new Error(`getFieldDef failed: no worksheet def ${wsName}`);
  }
  const fieldDef = _.where(wsDef, { field });
  if (!fieldDef.length) {
    throw new Error(`getFieldDef failed: no field ${field} in worksheet def ${wsName}`);
  }
  if (fieldDef.length > 1) {
    throw new Error(`getFieldDef failed: more than one field ${field} in worksheet def ${wsName}`);
  }
  return fieldDef[0];
};

export const getFieldNames = (wsName): string[] => {
  const wsDef = WS_DEF[wsName];
  if (!wsDef) {
    throw new Error(`getFieldNames failed: no worksheet def ${wsName}`);
  }
  const fields = _.pluck(wsDef, "field");
  if (!fields.length) {
    throw new Error(`getFieldNames failed: no fields in worksheet def ${wsName}`);
  }
  return fields;
};
